import React, { useState } from 'react';
import { Box, Paper, Typography, Collapse, IconButton, Chip, LinearProgress, useTheme, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MarkdownContent from './markdownContent';

const StepItem = ({ step, isLast, isFirst }) => {
    const [expanded, setExpanded] = useState(step.status === 'executing');
    const theme = useTheme();

    // Auto-expand if executing
    React.useEffect(() => {
        if (step.status === 'executing') {
            setExpanded(true);
        }
    }, [step.status]);

    const getStatusColor = () => {
        switch (step.status) {
            case 'completed':
                return theme.palette.success.main;
            case 'executing':
                return theme.palette.primary.main;
            case 'failed':
                return theme.palette.error.main;
            default:
                return theme.palette.grey[400];
        }
    };

    const getStatusIcon = () => {
        const color = getStatusColor();
        const sx = { color, fontSize: 20, bgcolor: 'background.paper', zIndex: 1 };

        switch (step.status) {
            case 'completed':
                return <CheckCircleIcon sx={sx} />;
            case 'executing':
                return (
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                animation: 'pulse 1.5s infinite ease-in-out',
                                border: `2px solid ${color}`,
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)', opacity: 1 },
                                    '50%': { transform: 'scale(1.5)', opacity: 0 },
                                    '100%': { transform: 'scale(1)', opacity: 0 },
                                },
                            }}
                        />
                        <PlayCircleOutlineIcon
                            sx={{
                                ...sx,
                                animation: 'spin 2s linear infinite',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' },
                                },
                            }}
                        />
                    </Box>
                );
            case 'failed':
                return <ErrorIcon sx={sx} />;
            default:
                return <RadioButtonUncheckedIcon sx={sx} />;
        }
    };

    return (
        <Box sx={{ position: 'relative', display: 'flex', mb: 0 }}>
            {/* Vertical Line */}
            {!isLast && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 9,
                        top: 24,
                        bottom: -4,
                        width: 2,
                        bgcolor: step.status === 'completed' ? getStatusColor() : theme.palette.grey[200],
                        zIndex: 0,
                    }}
                />
            )}

            <Box sx={{ mr: 2, mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {getStatusIcon()}
            </Box>

            <Box sx={{ flex: 1, pb: 2, minWidth: 0 }}>
                <Paper
                    elevation={0}
                    variant="outlined"
                    sx={{
                        border: expanded ? `1px solid ${getStatusColor()}80` : `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        backgroundColor:
                            step.status === 'executing' ? `${theme.palette.primary.main}05` : 'background.paper',
                        '&:hover': {
                            borderColor: step.status !== 'executing' && theme.palette.grey[400],
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        },
                    }}
                >
                    <Box
                        onClick={() => setExpanded(!expanded)}
                        sx={{
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                            {step.step}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                                label={step.status}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: `${getStatusColor()}15`,
                                    color: getStatusColor(),
                                    textTransform: 'capitalize',
                                    fontWeight: 600,
                                }}
                            />
                            {expanded ? (
                                <KeyboardArrowUpIcon fontSize="small" color="action" />
                            ) : (
                                <KeyboardArrowDownIcon fontSize="small" color="action" />
                            )}
                        </Box>
                    </Box>

                    <Collapse in={expanded}>
                        <Box sx={{ px: 1.5, pb: 1.5 }}>
                            {step.status === 'executing' && (
                                <LinearProgress
                                    sx={{
                                        mb: 1.5,
                                        height: 2,
                                        borderRadius: 1,
                                        bgcolor: `${theme.palette.primary.main}20`,
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            )}

                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: theme.palette.grey[50],
                                    borderRadius: 1.5,
                                    border: `1px solid ${theme.palette.grey[100]}`,
                                    '& .ai-message-markdown p': {
                                        fontSize: '0.85rem',
                                        color: theme.palette.text.secondary,
                                        mb: 1,
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                                    },
                                    '& .ai-message-markdown table': {
                                        fontSize: '0.8rem',
                                    },
                                }}
                            >
                                <MarkdownContent content={step.content || 'Processing...'} />
                            </Box>

                            {step.plan_details && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.palette.text.secondary,
                                            display: 'block',
                                            mb: 0.5,
                                        }}
                                    >
                                        Execution Plan
                                    </Typography>
                                    <Box
                                        sx={{
                                            p: 1,
                                            bgcolor: `${theme.palette.info.main}08`,
                                            borderRadius: 1,
                                            border: `1px solid ${theme.palette.info.main}20`,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontFamily: 'Consolas, Monaco, monospace',
                                                color: theme.palette.text.secondary,
                                                whiteSpace: 'pre-wrap',
                                            }}
                                        >
                                            {step.plan_details}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </Paper>
            </Box>
        </Box>
    );
};

const IntermediateSteps = ({ steps = [], isCollapsed, onToggle, question = '' }) => {
    const theme = useTheme();

    if (!steps || steps.length === 0) return null;

    const completedCount = steps.filter((s) => s.status === 'completed').length;
    const isExecuting = steps.some((s) => s.status === 'executing');
    const hasSteps = steps.length > 0;

    // Compact styling for collapsed state
    if (isCollapsed) {
        return (
            <Box sx={{ display: 'flex', mb: 2 }}>
                <Paper
                    elevation={0}
                    onClick={onToggle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        pl: 1,
                        pr: 2,
                        py: 0.75,
                        borderRadius: 50,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: isExecuting ? `${theme.palette.primary.main}08` : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        maxWidth: '100%',
                        '&:hover': {
                            bgcolor: isExecuting ? `${theme.palette.primary.main}15` : theme.palette.action.hover,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transform: 'translateY(-1px)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: isExecuting ? theme.palette.primary.main : theme.palette.grey[200],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isExecuting ? 'white' : theme.palette.text.secondary,
                        }}
                    >
                        {isExecuting ? (
                            <PlayCircleOutlineIcon sx={{ fontSize: 18, animation: 'spin 2s linear infinite' }} />
                        ) : (
                            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.2 }}>
                            Thinking Process
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                        >
                            {completedCount}/{steps.length} steps
                        </Typography>
                    </Box>

                    <KeyboardArrowDownIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.7 }} />
                </Paper>
            </Box>
        );
    }

    // Expanded State
    return (
        <Paper
            elevation={0}
            variant="outlined"
            sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                borderColor: isExecuting ? theme.palette.primary.main : theme.palette.grey[300],
                borderWidth: '1px',
                transition: 'all 0.3s ease',
                maxWidth: '100%',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{
                    p: 2,
                    bgcolor: isExecuting ? `${theme.palette.primary.main}08` : theme.palette.grey[50],
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: isExecuting ? theme.palette.primary.main : theme.palette.grey[200],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isExecuting ? '#fff' : theme.palette.text.secondary,
                        }}
                    >
                        {isExecuting ? (
                            <PlayCircleOutlineIcon sx={{ fontSize: 20, animation: 'spin 2s linear infinite' }} />
                        ) : (
                            <Typography variant="caption" fontWeight="bold">
                                {steps.length}
                            </Typography>
                        )}
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Thinking Process
                        </Typography>
                        {question && (
                            <Tooltip title={question}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        maxWidth: '400px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {question}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                <IconButton size="small">
                    <ExpandLessIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 2, maxHeight: '500px', overflowY: 'auto', bgcolor: '#fff' }}>
                {steps.map((step, index) => (
                    <StepItem key={index} step={step} isFirst={index === 0} isLast={index === steps.length - 1} />
                ))}
            </Box>
        </Paper>
    );
};

export default IntermediateSteps;
